export interface DiffLine {
  type: 'same' | 'added' | 'removed'
  text: string
}

// Standard LCS-based diff, generic over string tokens — used both for whole
// lines and, tokenized differently, for words within a single changed line.
// Plan outlines are short, so an O(n*m) table is more than fast enough and
// needs no dependency.
export function computeLineDiff(oldLines: string[], newLines: string[]): DiffLine[] {
  const m = oldLines.length
  const n = newLines.length
  const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0))

  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      lcs[i][j] =
        oldLines[i] === newLines[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1])
    }
  }

  const result: DiffLine[] = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    if (oldLines[i] === newLines[j]) {
      result.push({ type: 'same', text: oldLines[i] })
      i += 1
      j += 1
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      result.push({ type: 'removed', text: oldLines[i] })
      i += 1
    } else {
      result.push({ type: 'added', text: newLines[j] })
      j += 1
    }
  }
  while (i < m) {
    result.push({ type: 'removed', text: oldLines[i] })
    i += 1
  }
  while (j < n) {
    result.push({ type: 'added', text: newLines[j] })
    j += 1
  }
  return result
}

// Splits on word boundaries while keeping whitespace as its own token, so
// diffing and rejoining the tokens reproduces the original spacing exactly.
export function tokenizeWords(text: string): string[] {
  return text.split(/(\s+)/).filter((token) => token.length > 0)
}

export type RenderDiffItem =
  | { kind: 'same' | 'added' | 'removed'; text: string }
  | {
      kind: 'modified'
      oldText: string
      newText: string
      oldWords: DiffLine[]
      newWords: DiffLine[]
    }

// Pairs up an adjacent single removed line + single added line (in either
// order — the LCS backtrack can emit them either way) into one 'modified'
// entry with a word-level diff, so a changed line highlights only the words
// that actually changed instead of recoloring the whole line. Runs of more
// than one removed/added line in a row are left as plain whole-line
// add/remove entries — pairing those would be guessing which pairs with
// which.
export function buildDiffRenderItems(diffLines: DiffLine[]): RenderDiffItem[] {
  const items: RenderDiffItem[] = []
  let i = 0
  while (i < diffLines.length) {
    const current = diffLines[i]
    const next = diffLines[i + 1]
    const isPair =
      next &&
      ((current.type === 'removed' && next.type === 'added') ||
        (current.type === 'added' && next.type === 'removed')) &&
      diffLines[i + 2]?.type !== current.type &&
      diffLines[i - 1]?.type !== current.type

    if (isPair) {
      const oldText = current.type === 'removed' ? current.text : next.text
      const newText = current.type === 'added' ? current.text : next.text
      const wordDiff = computeLineDiff(tokenizeWords(oldText), tokenizeWords(newText))
      items.push({
        kind: 'modified',
        oldText,
        newText,
        oldWords: wordDiff.filter((word) => word.type !== 'added'),
        newWords: wordDiff.filter((word) => word.type !== 'removed')
      })
      i += 2
    } else {
      items.push({ kind: current.type, text: current.text })
      i += 1
    }
  }
  return items
}
